import LegalLayout from "./LegalLayout";

/**
 * SMS / Text Messaging Terms & Conditions.
 * Standalone page for A2P 10DLC (Twilio) campaign registration. Includes the
 * required SMS program disclosures: program/brand name, message types, opt-in,
 * message frequency, "Msg & data rates may apply", STOP/HELP, and support.
 */
export default function SmsTerms() {
  return (
    <LegalLayout
      title="SMS Terms & Conditions"
      subtitle="Legal · SMS Program Terms"
      lastUpdated="June 2026"
    >
      <p>
        These SMS Terms &amp; Conditions (<strong>"SMS Terms"</strong>) govern the
        TeamNest text-messaging program (the <strong>"Program"</strong>) operated
        by <strong>Funasia Digital LLC</strong>, doing business as
        <strong> TeamNest</strong> (<strong>"we"</strong>, <strong>"us"</strong>,
        <strong> "our"</strong>), the business behind the website{" "}
        <strong>teamnest.ai</strong> and the TeamNest web and mobile apps. By
        opting in to receive text messages from TeamNest, you agree to these SMS
        Terms and to our{" "}
        <a href="/privacy">Privacy Policy</a> and{" "}
        <a href="/terms">Terms of Service</a>.
      </p>

      <h2>1. Program description</h2>
      <p>
        When you provide your mobile number and opt in, TeamNest may send you
        recurring text (SMS/MMS) messages related to your account, including:
      </p>
      <ul>
        <li><strong>One-time passcodes and verification codes</strong> to sign in or verify your phone number.</li>
        <li><strong>Account and security alerts</strong> (e.g. new device sign-in, password changes).</li>
        <li><strong>Notifications and reminders</strong> you choose to enable, such as chat mentions, task due dates, and meeting reminders.</li>
      </ul>
      <p>
        We do <strong>not</strong> send marketing or promotional text messages
        unless you separately and expressly opt in to them.
      </p>

      <h2>2. How you opt in</h2>
      <p>
        Enrollment is voluntary. You opt in by entering your mobile number in
        TeamNest (for example during sign-up or in{" "}
        <strong>Settings → Notifications</strong>) and confirming that you agree
        to receive text messages at that number. Consent to receive text
        messages is <strong>not</strong> a condition of purchasing any goods or
        services.
      </p>

      <h2>3. Message frequency</h2>
      <p>
        Message frequency varies and depends on your activity and the
        notifications you enable. For example, you will receive a message each
        time you request a verification code, and reminder messages only for the
        events you have opted in to.
      </p>

      <h2>4. Cost</h2>
      <p>
        <strong>Message and data rates may apply.</strong> TeamNest does not
        charge for the messages, but your mobile carrier's standard message and
        data rates apply. Check with your carrier for details on your plan.
      </p>

      <h2>5. Opt out — reply STOP</h2>
      <p>
        You can cancel the Program at any time by texting{" "}
        <strong>STOP</strong> to the number from which you received our message.
        After you send <strong>STOP</strong>, we will send you a one-time
        confirmation message and then stop sending SMS messages. You may also
        turn off SMS notifications in <strong>Settings → Notifications</strong>.
        To rejoin, opt in again as described above.
      </p>

      <h2>6. Help — reply HELP</h2>
      <p>
        For help at any time, reply <strong>HELP</strong> to any message from us,
        or contact us at{" "}
        <a href="mailto:support@teamnest.ai">support@teamnest.ai</a>. We will
        reply with support information and how to opt out.
      </p>

      <h2>7. Carriers</h2>
      <p>
        Carriers are not liable for delayed or undelivered messages. Message
        delivery is subject to effective transmission from your wireless service
        provider and is outside of our control.
      </p>

      <h2>8. Supported carriers</h2>
      <p>
        The Program is available on major U.S. carriers. Carriers are not
        required to participate and may change supported services at any time.
      </p>

      <h2>9. Privacy</h2>
      <p>
        Your mobile number and messaging consent are handled as described in our{" "}
        <a href="/privacy">Privacy Policy</a>. <strong>We do not sell your
        personal information, and no mobile opt-in information or consent is
        shared with third parties or affiliates for their marketing or
        promotional purposes.</strong>
      </p>

      <h2>10. Changes</h2>
      <p>
        We may update these SMS Terms from time to time. Material changes take
        effect when posted on this page.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about the Program? Email{" "}
        <a href="mailto:support@teamnest.ai">support@teamnest.ai</a>. Postal mail:
        Funasia Digital LLC (d/b/a TeamNest), 1111B S Governors Ave Ste 6433,
        Dover DE 19904, USA.
      </p>
    </LegalLayout>
  );
}
